import React, { useEffect, useState } from 'react';
import { Box, Autocomplete, FormGroup, FormControlLabel, Switch, Checkbox, TextField } from '@mui/material';
import CustomButton from 'shared/custom-button/CustomButton';
import { colors } from 'app/providers/theme/colors-type';
import styles from 'features/cancer-protection/step-2/ui/Insured.module.css';
import { IStepTwo } from 'entities/cancer-protection/step-2/types/StepTwo-inerface';
import { RoundedTextField } from 'shared/rounded-textField/RoundedTextField';
import { RoundedAutocomplete } from 'shared/basic-autocomplete/RoundedAutocomplete';
import { NestedKeyOf } from 'shared/utils/hooks/useFormRedux';
import { FormFieldsStepOne } from 'entities/cancer-protection/step-1/selector';
import { useAppDispatch, useAppSelector } from 'shared/utils/hooks/useRedux';
import { updateField } from 'entities/cancer-protection/step-2';
import InfoIcon from '@mui/icons-material/Info';


interface InsuredProps {
    formState: IStepTwo;
    errors: Record<string, string>;
    handleBack: () => void;
    handleChange: (fieldName: NestedKeyOf<IStepTwo> | keyof IStepTwo) => (e: React.ChangeEvent<HTMLInputElement>) => void;
    handleBlur: (fieldName: NestedKeyOf<IStepTwo> | keyof IStepTwo) => (e: React.FocusEvent<HTMLInputElement | HTMLTextAreaElement>) => void;
    handleCheckboxChange: (fieldName: NestedKeyOf<IStepTwo> | keyof IStepTwo) => (e: React.ChangeEvent<HTMLInputElement>, checked: boolean) => void;
    handleArrayDateChange: (fieldName: string) => (e: React.ChangeEvent<HTMLInputElement>) => void;
    handleNext: () => void;
    formatPhone: (phone: string) => string;
    handleArrayPhoneChange: (fieldName: NestedKeyOf<IStepTwo> | keyof IStepTwo) => (e: React.ChangeEvent<HTMLInputElement>) => void;
}

export const Insured: React.FC<InsuredProps> = ({
    formState,
    errors,
    handleBack,
    handleChange,
    handleBlur,
    handleCheckboxChange,
    handleArrayDateChange,
    handleNext,
    formatPhone,
    handleArrayPhoneChange
}) => {
    const fieldsStepOne = useAppSelector(FormFieldsStepOne);
    const dispatch = useAppDispatch();

    useEffect(() => {
        if (fieldsStepOne.InsuredBirthDate !== formState.Insurer.BirthDate) {
            dispatch(updateField({ field: 'PolicyholderIsSame', value: false }));
        }
    }, [fieldsStepOne.InsuredBirthDate]);

    const [errorBirthDate, setErrorBirthDate] = useState<string | null>(null);

    const handlePolicyholderIsSameChange = (e: React.ChangeEvent<HTMLInputElement>, checked: boolean) => {
        handleCheckboxChange('PolicyholderIsSame')(e, checked);
        if (checked && fieldsStepOne.InsuredBirthDate !== formState.Insurer.BirthDate) {
            setErrorBirthDate(`Дата рождения указанная на этапе расчета отличается от даты рождения страхователя, необходимо вернуться на этап расчета для корректировки`);
        } else if (!checked && fieldsStepOne.InsuredBirthDate !== formState.Insureds[0].BirthDate) {
            setErrorBirthDate(`Дата рождения указанная на этапе расчета отличается от даты рождения застрахованного, необходимо вернуться на этап расчета для корректировки`);
        } else {
            setErrorBirthDate(null);
        }
    };

    useEffect(() => {
        if (!formState.PolicyholderIsSame && formState.Insureds[0].BirthDate && fieldsStepOne.InsuredBirthDate !== formState.Insureds[0].BirthDate) {
            setErrorBirthDate(`Дата рождения указанная на этапе расчета отличается от даты рождения застрахованного, необходимо вернуться на этап расчета для корректировки`);
        } else if (errorBirthDate && (fieldsStepOne.InsuredBirthDate === formState.Insurer.BirthDate || fieldsStepOne.InsuredBirthDate === formState.Insureds[0].BirthDate)) {
            setErrorBirthDate(null);
        }
    }, [fieldsStepOne.InsuredBirthDate, errorBirthDate, formState.Insureds[0].BirthDate, formState.Insurer.BirthDate]);

    return (
        <Box>
            <Box className={styles.formBox}>
                <Box className={styles.titleBox}>
                    <h4>Застрахованный</h4>
                    <Box className={styles.fullWidthBox}>
                        <FormControlLabel
                            control={
                                <Checkbox
                                    name="PolicyholderIsSame"
                                    checked={Boolean(formState.PolicyholderIsSame)}
                                    onChange={handlePolicyholderIsSameChange}
                                    sx={{
                                        color: 'grey.500',
                                        '&.Mui-checked': {
                                            color: colors.primary,
                                        },
                                    }}
                                />
                            }
                            label="Буду являться застрахованным"
                        />
                    </Box>
                </Box>
                {!formState.PolicyholderIsSame && (
                    <Box className={styles.formFields}>
                        <Box className={styles.twoLine}>
                            <TextField
                                name={`Insureds[0].LastName`}
                                label="Фамилия"
                                value={formState.Insureds[0].LastName}
                                onChange={handleChange('Insureds[0].LastName')}
                                onBlur={(e) => {
                                    handleBlur(`Insureds[0].LastName`)(e);
                                }}
                                error={!!errors[`Insureds[0].LastName`]}
                                helperText={errors[`Insureds[0].LastName`]}
                            />
                            <TextField
                                name={`Insureds[0].FirstName`}
                                label="Имя"
                                value={formState.Insureds[0].FirstName}
                                onChange={handleChange('Insureds[0].FirstName')}
                                onBlur={(e) => {
                                    handleBlur(`Insureds[0].FirstName`)(e);
                                }}
                                error={!!errors[`Insureds[0].FirstName`]}
                                helperText={errors[`Insureds[0].FirstName`]}
                            />
                        </Box>
                        <Box className={styles.twoLine}>
                            <TextField
                                name={`Insureds[0].MiddleName`}
                                label="Отчество"
                                value={formState.Insureds[0].MiddleName}
                                onChange={handleChange('Insureds[0].MiddleName')}
                                onBlur={(e) => {
                                    handleBlur('Insureds[0].MiddleName')(e);
                                }}
                                error={!!errors['Insureds[0].MiddleName']}
                                helperText={errors['Insureds[0].MiddleName']}
                            />
                            <TextField
                                name={`Insureds[0].BirthDate`}
                                label="Дата рождения"
                                value={formState.Insureds[0].BirthDate}
                                placeholder="дд.мм.гггг"
                                onChange={handleArrayDateChange('Insureds[0].BirthDate')}
                                onBlur={(e) => {
                                    handleBlur('Insureds[0].BirthDate')(e);
                                }}
                                error={!!errors['Insureds[0].BirthDate']}
                                helperText={errors['Insureds[0].BirthDate']}
                            />
                        </Box>
                        <Box className={styles.twoLine}>
                            <TextField
                                name={`Insureds[0].Phone`}
                                label="Телефон"
                                value={formatPhone(formState.Insureds[0].Phone)}
                                onChange={handleArrayPhoneChange('Insureds[0].Phone')}
                                onBlur={(e) => {
                                    handleBlur('Insureds[0].Phone')(e);
                                }}
                                error={!!errors['Insureds[0].Phone']}
                                helperText={errors['Insureds[0].Phone']}
                            />
                        </Box>
                    </Box>
                )}
            </Box>

            {errorBirthDate && (
                <Box className={styles.errorBox}>
                    <span className={styles.iconRed}><InfoIcon /></span>
                    <Box className={styles.textErrorBox}>
                        <p>{errorBirthDate}</p>
                    </Box>
                </Box>
            )}

            <Box className={styles.btnBoxNext}>
                <CustomButton
                    title="Назад"
                    handleClick={handleBack}
                    textColor={colors.primary}
                    backgroundColor="none"
                    width="150px"
                    gap="10px"
                    borderRadius='4px'
                    padding='3px'
                    border='none'
                />
                {!errorBirthDate && (
                    <CustomButton
                        title="Далее"
                        handleClick={handleNext}
                        textColor="white"
                        backgroundColor={colors.primary}
                        width="150px"
                        gap="10px"
                        borderRadius='4px'
                        padding='3px'
                    />
                )}
            </Box>
        </Box>
    );
};
